import Plugin from '../core/Plugin';
import * as SelectionUtils from '../SelectionUtils';

/**
 * FontSizePlugin - Provides font size selection via dropdown and custom input
 */
class FontSizePlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'FontSize';
  }

  getToolbarButtons() {
    return [
      {
        name: 'font-size',
        dropdown: {
          command: 'font-size',
          label: 'Font Size',
          title: 'Select Font Size',
          options: [
            { value: '8pt', label: '8pt' },
            { value: '10pt', label: '10pt' },
            { value: '12pt', label: '12pt' },
            { value: '13pt', label: '13pt' },
            { value: '13.5pt', label: '13.5pt' },
            { value: '14pt', label: '14pt' },
            { value: '16pt', label: '16pt' },
            { value: '18pt', label: '18pt' },
            { value: '20pt', label: '20pt' },
            { value: '24pt', label: '24pt' },
            { value: '28pt', label: '28pt' },
            { value: '32pt', label: '32pt' },
            { value: '36pt', label: '36pt' },
            { value: '48pt', label: '48pt' },
          ],
        },
      },
    ];
  }

  /**
   * Apply font size to math elements (KaTeX and MathML) within a container
   * @param {Element} container - The container element to search within
   * @param {string} size - The font size to apply (e.g., '24pt')
   */
  applyFontSizeToMathElements(container, size) {
    if (!container) return;

    // Find all math elements (both KaTeX and MathML)
    const mathElements = container.querySelectorAll('.quillkit-math, .quillkit-mathml');
    mathElements.forEach((mathEl) => {
      // Apply font-size directly to the math wrapper element
      // This works because KaTeX uses em-based sizing internally,
      // so setting font-size on the wrapper scales the entire equation
      if (mathEl && mathEl.style) {
        mathEl.style.fontSize = size;
      }
    });
  }

  executeCommand(command) {
    // Extract the size from the command (e.g., 'font-size-12pt' -> '12pt')
    const sizeMatch = command.match(/^font-size-(.+)$/);
    if (sizeMatch) {
      const size = sizeMatch[1];
      // Validate size format to prevent CSS injection (must be number followed by pt)
      if (!/^\d+(\.\d+)?pt$/.test(size)) {
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);

      // Helper function to get element from node
      const getElementNode = (node) => {
        return node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      };

      // Helper function to check if an element is or contains math
      const hasMathContent = (element) => {
        if (!element) return false;

        // Check if element itself is a math element
        if (element.classList?.contains('quillkit-math') || element.tagName === 'MATH') {
          return true;
        }

        // Check if element contains math elements
        if (
          element.querySelector &&
          (element.querySelector('.quillkit-math') !== null ||
            element.querySelector('math') !== null)
        ) {
          return true;
        }

        // Walk up DOM tree to check if we're inside a math element (limit to 20 levels)
        let node = element;
        let depth = 0;
        while (node && node !== document.body && depth < 20) {
          if (node.classList?.contains('quillkit-math') || node.tagName === 'MATH') {
            return true;
          }
          node = node.parentElement;
          depth++;
        }

        return false;
      };

      // Restrict effect to the editor's text area only
      // Check for richEditor (used by QuillKit), root, or fallback to .quillkit-editor class
      const editorRoot = 
        (this.editor && this.editor.richEditor) || 
        (this.editor && this.editor.root) || 
        document.querySelector('.quillkit-editor');
      if (!editorRoot) {
        return;
      }

      // Ensure the selection is within the editor's text area
      const containerElement = getElementNode(range.commonAncestorContainer);
      if (!editorRoot.contains(containerElement)) {
        return;
      }

      // Only apply font size to selection inside the editor area
      SelectionUtils.wrapSelection('span', { style: `font-size: ${size};` });

      // If selection contains math elements, apply font size directly to them as well
      if (hasMathContent(containerElement)) {
        const selectionAfter = window.getSelection();
        if (selectionAfter && selectionAfter.rangeCount > 0) {
          const newRange = selectionAfter.getRangeAt(0);
          let node = newRange.commonAncestorContainer;
          if (node.nodeType !== Node.ELEMENT_NODE) {
            node = node.parentElement;
          }
          // Only apply to math elements inside the editor
          if (editorRoot.contains(node)) {
            this.applyFontSizeToMathElements(node, size);
          }
        }
      }
    }
  }

  getMetadata() {
    return {
      name: 'Font Size',
      version: '1.0.1',
      description: 'Provides font size selection via dropdown and custom input',
      author: 'AtlaraFirm',
    };
  }
}

export default FontSizePlugin;
